import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Check,
  MoreVert,
  SkipNext,
  History,
  Info,
  StickyNote2Outlined,
  ReportProblemOutlined,
} from '@mui/icons-material'
import { FC, useMemo, useState } from 'react'
import { decode } from 'html-entities'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { RepeatType } from 'src/enums'
import { IMaintenanceListItem } from 'src/generated-types/scheduled-maintenance'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor, getFormattedDate } from 'src/utils'
import { formatItemIdentification } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { TitleWithTooltip } from './TitleWithTooltip'
import { RichTextViewer } from 'src/components/common/RichTextViewer'

const REPEAT_TYPE_SINGLE_KEY: Record<number, string> = {
  [RepeatType.Never]: 'never',
  [RepeatType.Daily]: 'daily',
  [RepeatType.Weekly]: 'weekly',
  [RepeatType.Monthly]: 'monthly',
  [RepeatType.Yearly]: 'yearly',
}

const REPEAT_TYPE_PLURAL_KEY: Record<number, string> = {
  [RepeatType.Daily]: 'n-days',
  [RepeatType.Weekly]: 'n-weeks',
  [RepeatType.Monthly]: 'n-months',
  [RepeatType.Yearly]: 'n-years',
}

interface MaintenanceListRowProps {
  item: IMaintenanceListItem
  isSelected: boolean
  onToggleSelect: (id: number) => void
  onQuickPerform: (item: IMaintenanceListItem) => void
  onSkip: (item: IMaintenanceListItem) => void
  onViewHistory?: (item: IMaintenanceListItem) => void
  onPerformWithNote: (item: IMaintenanceListItem) => void
  onCreateDeviation?: (item: IMaintenanceListItem) => void
  performingRowId?: number | null
  isModernLayout?: boolean
}

const MaintenanceListRow: FC<MaintenanceListRowProps> = ({
  item,
  isSelected,
  onToggleSelect,
  onQuickPerform,
  onSkip,
  onViewHistory,
  onPerformWithNote,
  onCreateDeviation,
  performingRowId = null,
  isModernLayout = false,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['maintenance', 'common'])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getLocationText = () => {
    if (item.isLocalityTask) return t('locality-task', { ns: 'maintenance' })
    return formatItemIdentification({
      itemType: item.mainComponentTypeName ? getTranslatedText(item.mainComponentTypeName) : undefined,
      positionName: item.positionName,
      positionReference: item.positionReference,
      positionNote: item.positionNote,
      mainComponentReference: item.mainComponentReference,
      serialNumber: item.serialNumber,
    })
  }

  const frequencyLabel = useMemo(() => {
    const freq = parseInt(item.frequency ?? '1', 10) || 1
    const repeatType = item.repeatType
    if (repeatType === RepeatType.Never || freq <= 1) {
      return t(REPEAT_TYPE_SINGLE_KEY[repeatType] ?? 'never', { ns: 'maintenance' })
    }
    const pluralKey = REPEAT_TYPE_PLURAL_KEY[repeatType]
    if (!pluralKey) return t(REPEAT_TYPE_SINGLE_KEY[repeatType] ?? 'never', { ns: 'maintenance' })
    return t(pluralKey, { ns: 'maintenance', count: freq })
  }, [t, item.frequency, item.repeatType])

  const getOverdueText = () => {
    if (!item.isOverdue) return null
    if (item.daysOverdue === 1) return `1d ${t('overdue', { ns: 'maintenance' })}`
    return `${item.daysOverdue}d ${t('overdue', { ns: 'maintenance' })}`
  }

  const borderLeftColor = item.isOverdue
    ? theme.palette.error.main
    : item.isDueToday
    ? theme.palette.primary.main
    : 'transparent'

  const isUpcoming = !item.isOverdue && !item.isDueToday
  const scheduledDateLabel = item.scheduledDate ? getFormattedDate(new Date(item.scheduledDate)) : null

  const maintenanceNoteHtml = useMemo(
    () => (item.maintenanceNote?.trim() ? decode(item.maintenanceNote) : ''),
    [item.maintenanceNote],
  )

  const isPerforming = performingRowId === item.id

  if (isModernLayout) {
    const noteTooltipProps = {
      placement: 'top-start' as const,
      enterDelay: 300,
      componentsProps: {
        tooltip: {
          sx: {
            maxWidth: 380,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 1.5,
          },
        },
      },
    }

    return (
      <ModernRowContainer borderLeftColor={borderLeftColor}>
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect(item.id)}
          size="small"
          sx={{ padding: '4px', mr: 1 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TitleWithTooltip title={item.maintenanceName} />
            </Box>
            <Typography
              variant="caption"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', textTransform: 'capitalize', textAlign: 'right' }}
            >
              {item.isOverdue && (
                <Box component="span" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                  {getOverdueText()}
                </Box>
              )}
              {item.isOverdue && (
                <Box component="span" sx={{ color: theme.palette.text.secondary, mx: 0.5 }}>
                  |
                </Box>
              )}
              {isUpcoming && scheduledDateLabel && (
                <>
                  <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {scheduledDateLabel}
                  </Box>
                  <Box component="span" sx={{ color: theme.palette.text.secondary, mx: 0.5 }}>
                    |
                  </Box>
                </>
              )}
              <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                {frequencyLabel}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, mt: '2px' }}>
            <Tooltip title={getLocationText()} placement="top-start" enterDelay={300}>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: '0 1 auto', minWidth: 0, mr: 'auto' }}
              >
                {getLocationText()}
              </Typography>
            </Tooltip>
            {maintenanceNoteHtml && (
              <Tooltip
                title={
                  <RichTextViewer
                    html={maintenanceNoteHtml}
                    maxHeight={300}
                    sx={{
                      width: 340,
                      '& .ql-editor': { padding: 0, fontSize: '0.8125rem' },
                      '& .ql-container.ql-snow': { border: 'none' },
                    }}
                  />
                }
                {...noteTooltipProps}
              >
                <IconButton size="small" sx={{ p: '2px', flexShrink: 0, color: 'text.secondary' }}>
                  <StickyNote2Outlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1, alignSelf: 'center' }}>
          {item.isPerformable && (
            <GridActionButton
              onClick={() => onQuickPerform(item)}
              variant="contained"
              bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
              width="64px"
              height="32px"
              shadowWeight={0}
              border={0}
              loading={isPerforming}
              disabled={isPerforming}
              buttonText={isPerforming ? undefined : t('sign', { ns: 'common' })}
              buttonTextProps={{
                variant: 'body2',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: getThemeColor(theme, MUIThemeColors.white),
              }}
            />
          )}
          <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {item.isPerformable && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  onPerformWithNote(item)
                }}
              >
                <Info fontSize="small" sx={{ mr: 1 }} />
                {t('done-with-note', { ns: 'maintenance' })}
              </MenuItem>
            )}
            {onCreateDeviation && item.mainComponentId != null && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  onCreateDeviation(item)
                }}
              >
                <ReportProblemOutlined fontSize="small" sx={{ mr: 1 }} />
                {t('register-deviation', { ns: 'deviation' })}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                onSkip(item)
              }}
            >
              <SkipNext fontSize="small" sx={{ mr: 1 }} />
              {t('skip', { ns: 'maintenance' })}
            </MenuItem>
            {onViewHistory && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  onViewHistory(item)
                }}
              >
                <History fontSize="small" sx={{ mr: 1 }} />
                {t('history', { ns: 'common' })}
              </MenuItem>
            )}
          </Menu>
        </Box>
      </ModernRowContainer>
    )
  }

  return (
    <RowContainer borderLeftColor={borderLeftColor}>
      <Checkbox
        checked={isSelected}
        onChange={() => onToggleSelect(item.id)}
        size="small"
        sx={{ padding: isMobile ? '6px' : '4px', marginRight: isMobile ? '4px' : '8px' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ flex: 1, fontSize: isMobile ? '0.8rem' : undefined }}
          >
            {item.maintenanceName}
          </Typography>
          {!isMobile && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {frequencyLabel}
            </Typography>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '2px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
        >
          <Tooltip title={getLocationText()} placement="top-start" enterDelay={300}>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{
                flex: isMobile ? 'unset' : '0 1 auto',
                mr: isMobile ? undefined : 'auto',
                maxWidth: isMobile ? '100%' : undefined,
                fontSize: isMobile ? '0.7rem' : undefined,
              }}
            >
              {getLocationText()}
            </Typography>
          </Tooltip>
          {item.isOverdue && (
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                color: theme.palette.error.main,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontSize: isMobile ? '0.7rem' : undefined,
              }}
            >
              {getOverdueText()}
            </Typography>
          )}
          {!isMobile && item.tags && item.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {item.tags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: isMobile ? 0.5 : 1, flexShrink: 0 }}>
        {item.isPerformable && (
          <IconButton
            size="small"
            onClick={() => onQuickPerform(item)}
            disabled={isPerforming}
            sx={{
              backgroundColor: theme.palette.success.main,
              color: theme.palette.common.white,
              width: isMobile ? 36 : 32,
              height: isMobile ? 36 : 32,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
              },
            }}
          >
            {isPerforming ? <CircularProgress size={16} color="inherit" /> : <Check fontSize="small" />}
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {item.isPerformable && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                onPerformWithNote(item)
              }}
            >
              <Info fontSize="small" sx={{ mr: 1 }} />
              {t('done-with-note', { ns: 'maintenance' })}
            </MenuItem>
          )}
          {onCreateDeviation && item.mainComponentId != null && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                onCreateDeviation(item)
              }}
            >
              <ReportProblemOutlined fontSize="small" sx={{ mr: 1 }} />
              {t('register-deviation', { ns: 'deviation' })}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              onSkip(item)
            }}
          >
            <SkipNext fontSize="small" sx={{ mr: 1 }} />
            {t('skip', { ns: 'maintenance' })}
          </MenuItem>
          {onViewHistory && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                onViewHistory(item)
              }}
            >
              <History fontSize="small" sx={{ mr: 1 }} />
              {t('history', { ns: 'common' })}
            </MenuItem>
          )}
        </Menu>
      </Box>
    </RowContainer>
  )
}

export default MaintenanceListRow

interface RowContainerProps {
  borderLeftColor: string
}

const RowContainer = styled(Box)<RowContainerProps>(({ theme, borderLeftColor }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${borderLeftColor}`,
  minHeight: 48,
  [theme.breakpoints.down('sm')]: {
    padding: '10px 8px',
    minHeight: 52,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const ModernRowContainer = styled(Box)<RowContainerProps>(({ theme, borderLeftColor }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `4px solid ${borderLeftColor !== 'transparent' ? borderLeftColor : theme.palette.divider}`,
  minHeight: 56,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))
